/* eslint-disable lodash/prefer-lodash-method */
import React, { Component } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Input, Label, Icon, Form, Popup } from 'semantic-ui-react';

import { validateHashtags } from './utils';

const inputStyle = { marginBottom: 10 };
const inputAction = { icon: 'plus' };
const requiredLabelStyle = { color: 'red' };
const tagStyles = { marginTop: 4, marginRight: 4, border: 'none' };
const HASHTAG_BG = '#fbe4ec';
const MENTION_BG = '#ececec';

const tagPalette = tag => (_.startsWith(tag, '@')
  ? { background: MENTION_BG, color: '#555555' }
  : { background: HASHTAG_BG, color: '#c2185b' });

const legendStyle = { display: 'flex', marginTop: 12, fontSize: '0.9em', color: '#767676' };
const legendItemStyle = { display: 'flex', alignItems: 'center', marginRight: 16 };
const swatchStyle = { width: 12, height: 12, borderRadius: 3, marginRight: 6, display: 'inline-block' };
const moreButtonStyle = { marginTop: 4, marginRight: 4, cursor: 'pointer', fontWeight: 'bold' };
const popupTagsStyle = { maxWidth: 520, maxHeight: 320, overflowY: 'auto' };
const COLLAPSE_LIMIT = 5;
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 };
// Restores Semantic's `.ui.form .field > label` styling, lost by nesting the label in the header div.
const labelStyle = { fontSize: '0.92857143em', fontWeight: 700, color: 'rgba(0,0,0,.87)' };
const countBadgeStyle = { fontWeight: 'bold', fontSize: '0.9em', whiteSpace: 'nowrap', marginLeft: 8 };

const countColor = (count, max) => {
  if (count >= max) return '#db2828';
  if (count >= max * 0.8) return '#f2711c';
  return '#767676';
};

const RequiredLabel = () => (<span style={requiredLabelStyle}>*</span>);

const HelpPopup = () => <Popup
  content="enter hashtags, @mentions or any text you want to use to capture the influencer posts.
    You can enter multiple entries separated by commas.
    The system will search the post or story caption and add the post to the campaign if it matches.
    Hashtags must be more than 2 characters"
  trigger={<Icon name="help circle" className="circular" />}
/>;

export default class HashtagsInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: '',
      tags: props.hashtags,
      error: null,
    };
  }

  onAddHashTagCallback = e => {
    e.preventDefault();
    const {
      maxTags,
      onValueChanged,
    } = this.props;
    const {
      value = '',
      tags,
    } = this.state;


    // Skip entries already present (or repeated within this paste); match case-insensitively.
    const seen = new Set(_.map(tags, t => _.toLower(t)));
    const newTags = [];
    let hadDuplicate = false;
    _.forEach(value.split(','), raw => {
      if (_.isEmpty(raw)) return;
      const key = _.toLower(raw);
      if (seen.has(key)) {
        hadDuplicate = true;
        return;
      }
      seen.add(key);
      newTags.push(raw);
    });

    if (_.size(tags) + _.size(newTags) > maxTags) {
      this.setState(state => ({
        ...state,
        error: `You can add up to ${maxTags} hashtags, mentions, or text entries only.` }),
      );
      return;
    }

    if (_.isEmpty(newTags)) {
      this.setState(state => ({
        ...state,
        error: hadDuplicate ? 'That hashtag or mention has already been added.' : state.error,
      }));
      return;
    }

    const tagsClone = [...tags, ...newTags];
    this.setState({
      tags: tagsClone,
      value: '',
      error: null,
    }, () => {
      onValueChanged(e, {
        name: 'hashtags',
        value: _.join(tagsClone, ','),
      });
    });
  }

 onTextInputChange = (e, { value }) => {
   this.setState({
     value: _.replace(value, / +/g, ''),
   });
 }

 resetInputError = () => {
   this.setState(state => ({ ...state, error: null }));
 }

  renderTag = (tag, i) => {
    const palette = tagPalette(tag);
    const chipStyle = { ...tagStyles, ...palette };
    const iconStyle = { color: palette.color };
    return (
      <Label key={`${tag}-${i}`} size="large" style={chipStyle}>
        { tag }
        <Icon name="close" link style={iconStyle} onClick={() => this.onDeleteTag(tag)} />
      </Label>
    );
  }

  onDeleteTag = tagToRemove => {
    // Defer removal a tick: deleting the chip synchronously unmounts it mid-click,
    // which Semantic's Popup reads as an outside click and closes the overflow popover.
    setTimeout(() => {
      const newTags = _.reject(this.state.tags, tag => tag === tagToRemove);
      this.setState({
        tags: newTags,
      }, () => {
        this.props.onValueChanged({}, {
          name: 'hashtags',
          value: _.join(newTags, ','),
        });
      });
    }, 0);
  }

  render() {
    const {
      maxTags,
      campaignType,
    } = this.props;

    const {
      value,
      tags,
      error,
    } = this.state;

    const hasOverflow = _.size(tags) > COLLAPSE_LIMIT;
    const visibleTags = hasOverflow ? _.take(tags, COLLAPSE_LIMIT) : tags;
    const hiddenTags = hasOverflow ? _.slice(tags, COLLAPSE_LIMIT) : [];

    const isAutomated = campaignType === 'AUTOMATED';

    const validationResult = validateHashtags(value);

    const isValidInput = validationResult.valid;

    const shouldDisableAddButton = _.toNumber(maxTags) === _.size(tags);

    const actionProps = {
      onClick: this.onAddHashTagCallback,
      ...inputAction,
      disabled: shouldDisableAddButton || !isValidInput,
    };

    return (
      <Form.Field>
        <div style={headerStyle}>
          <div>
            {
              isAutomated &&
              <label htmlFor="campaign-hashtags" style={labelStyle}>
                Tracked <i>#hashtag</i> or <i>@mention</i>
                <RequiredLabel />  <HelpPopup />
              </label>
            }
            {
              !isAutomated &&
              <label htmlFor="campaign-hashtags" style={labelStyle}>
                #Hashtags  <HelpPopup />
              </label>
            }
          </div>
          <span style={{ ...countBadgeStyle, color: countColor(_.size(tags), maxTags) }}>
            {_.size(tags)} / {maxTags}
          </span>
        </div>
        {
          error ? (
            <Label basic color="red" pointing="below">
              {error}
            </Label>) : null
        }
        <Input
          action={actionProps}
          onChange={this.onTextInputChange}
          placeholder={shouldDisableAddButton ? 'max tags added' : 'e.g. #nike'}
          value={value}
          onBlur={this.resetInputError}
          onFocus={this.resetInputError}
          style={inputStyle}
          disabled={shouldDisableAddButton}
        />
        {
          _.map(visibleTags, this.renderTag)
        }
        {
          hasOverflow &&
          <Popup
            on="click"
            wide="very"
            position="bottom left"
            trigger={
              <Label size="large" basic style={moreButtonStyle}>
                {`+${_.size(hiddenTags)} more`}
              </Label>
            }
            content={<div style={popupTagsStyle}>{_.map(hiddenTags, this.renderTag)}</div>}
          />
        }
        {
          !_.isEmpty(tags) &&
          <div style={legendStyle}>
            <span style={legendItemStyle}>
              <span style={{ ...swatchStyle, background: HASHTAG_BG }} /> hashtags
            </span>
            <span style={legendItemStyle}>
              <span style={{ ...swatchStyle, background: MENTION_BG }} /> mentions
            </span>
          </div>
        }
      </Form.Field>
    );
  }
}

HashtagsInput.propTypes = {
  maxTags: PropTypes.number,
  hashtags: PropTypes.array,
  onValueChanged: PropTypes.func.isRequired,
  campaignType: PropTypes.string,
};

HashtagsInput.defaultProps = {
  maxTags: 40,
  hashtags: [],
  campaignType: 'MANUAL',
};



/** WEBPACK FOOTER **
 ** ./src/components/hashtags-input/index.js
 **/